import React from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { LoadScript } from '@react-google-maps/api'
import type { Libraries } from '@react-google-maps/api'

const GMAPS_LIBRARIES: Libraries = ['places']

import { LoadingSpinner } from './components/ui/LoadingSpinner'
import Home from './pages/Home'
import { ForgotPassword, ResetPassword, SignIn, SignUp, VerifyEmail } from './pages/Auth'
import Dashboard from './pages/Dashboard'
import { BusinessOnboarding } from './pages/Onboarding'
import { SuccessPage } from './pages/Billing/SuccessPage'
import { CancelPage } from './pages/Billing/CancelPage'
import { LegalPage } from './pages/Legal/LegalPage'
import { apiGet } from './lib/api'
import { BUSINESS_ONBOARDING_STATUS } from './lib/endpoints'
import { clearAuth, loadAuth } from './lib/authStorage'
import type { OnboardingStatus } from './types'

function ProtectedDashboard() {
  const auth = loadAuth()
  const token = auth?.accessToken
  const isAdmin = auth?.role === 'ADMIN'
  const [status, setStatus] = React.useState<'checking' | 'allowed' | 'needs_onboarding'>(
    isAdmin ? 'allowed' : 'checking'
  )

  React.useEffect(() => {
    if (isAdmin || !token) {
      if (!isAdmin) setStatus('needs_onboarding')
      return
    }
    async function check() {
      try {
        const res = await apiGet<OnboardingStatus>(BUSINESS_ONBOARDING_STATUS)
        setStatus(res.onboardingCompleted ? 'allowed' : 'needs_onboarding')
      } catch {
        clearAuth()
        setStatus('needs_onboarding')
      }
    }
    check()
  }, [token, isAdmin])

  if (!token) return <Navigate to="/signin" replace />
  if (status === 'checking') return <LoadingSpinner variant="page" />
  if (status === 'needs_onboarding') return <Navigate to="/onboarding/business" replace />
  return <Dashboard />
}

function ProtectedOnboarding() {
  const auth = loadAuth()
  if (!auth?.accessToken) return <Navigate to="/signin" replace />
  return <BusinessOnboarding />
}

function ProtectedBillingPage({ page }: { page: 'success' | 'cancel' }) {
  const auth = loadAuth()
  if (!auth?.accessToken) return <Navigate to="/signin" replace />
  return page === 'success' ? <SuccessPage /> : <CancelPage />
}

export default function App() {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
  const googleMapsKey = import.meta.env.VITE_GOOGLE_MAPS_KEY

  const app = (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/dashboard" element={<ProtectedDashboard />} />
        <Route path="/onboarding/business" element={<ProtectedOnboarding />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/billing/success" element={<ProtectedBillingPage page="success" />} />
        <Route path="/billing/cancel" element={<ProtectedBillingPage page="cancel" />} />
        <Route path="/privacy" element={<LegalPage section="privacy" />} />
        <Route path="/terms" element={<LegalPage section="terms" />} />
      </Routes>
    </BrowserRouter>
  )

  const withMaps = googleMapsKey ? (
    <LoadScript googleMapsApiKey={googleMapsKey} libraries={GMAPS_LIBRARIES} loadingElement={<></>}>
      {app}
    </LoadScript>
  ) : app

  return googleClientId ? (
    <GoogleOAuthProvider clientId={googleClientId}>{withMaps}</GoogleOAuthProvider>
  ) : withMaps
}
